"use client";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { useParams } from "next/navigation";
import Image from "next/image";
import { allDepartmentsData } from "@/lib/allDepartments";
import DepartmentProfile from "../DepartmentDetailesTab/DepartmentProfile/DepartmentProfile";
import Organaisation from "../DepartmentDetailesTab/Organaisation/Organaisation";
import Hod from "../DepartmentDetailesTab/Hod/Hod";
import Faculty, { FacultyMember } from "../DepartmentDetailesTab/Faculty/Faculty";
import Academic from "../DepartmentDetailesTab/Academic/Academic";
import type { SyllabusItem } from "../DepartmentDetailesTab/AcademicSyllabusSchema/AcademicSyllabusSchema"; // <-- New Import
import dynamic from "next/dynamic";

const AcademicSyllabusSchema = dynamic(() => import("../DepartmentDetailesTab/AcademicSyllabusSchema/AcademicSyllabusSchema"), { ssr: false });
import Peo from "../DepartmentDetailesTab/Peo/Peo";
import CourseOutCome from "../DepartmentDetailesTab/CourseOutCome/CourseOutCome";
import Facilities from "../DepartmentDetailesTab/Facilities/Facilities";
import StudentAchievement from "../DepartmentDetailesTab/StudentAchievement/StudentAchievement";
import Research from "../DepartmentDetailesTab/Research/Research";
import Publications from "../DepartmentDetailesTab/Publications/Publications";
import Events from "../DepartmentDetailesTab/Events/Events";
import Gallery, { GalleryItem } from "../DepartmentDetailesTab/Gallery/Gallery";
import Magazines from "../DepartmentDetailesTab/Magazines/Magazines";
import CareerProspects from "../CareerProspects/CareerProspects";
import FacultyExcellence from "../DepartmentDetailesTab/FacultyExcellence/FacultyExcellence";

interface Qualification {
  degree: string;
  passingYear: number;
  collegeOrUniversity: string;
  areaOfSpecialization: string;
}
interface LocalFaculty {
  name: string;
  image: string;
  category: string;
  desiganation: string;
  department: string;
  joiningDate: string;
  experience: string;
  employmentType: string;
  qualifications: Qualification[];
}
// Interface for council member
interface CouncilMember {
  id: number;
  name: string;
  image?: string;
  hasAvatar?: boolean; // ✅ FIX 1: Added hasAvatar flag
  designation: string;
  category: string;
  department: string;
  joiningDate?: string; // Make this optional
  experience?: string; // Make this optional
  employmentType?: string; // Make this optional
  qualifications: Qualification[];
  faculties?: LocalFaculty[];
}

// Props interface for the component
interface DepartmentSectionProps {
  departmentName: string;
}

import CustomSelect from "@/components/Common/CustomSelect/CustomSelect";
import { ArrowLeftIcon } from "lucide-react";
import Link from "next/link";
import formatDepartmentName from "@/utils/formatDepartmentName";
import DepartmentalStructure from "../DepartmentalStructure/DepartmentalStructure";
import CourseOutComeAiDs from "../CourseOutComeAiDs/CourseOutComeAiDs";

// ✅ REMOVED bufferToBase64 function

const DepartmentDetailes = ({ departmentName }: DepartmentSectionProps) => {
  const { slug } = useParams();

  const [selectedIndex, setSelectedIndex] = useState<number>(0);
  const [selectedSection, setSelectedSection] = useState<string>("Department Profile");

  const [facultyData, setFacultyData] = useState<FacultyMember[]>([]);
  const [facultyLoading, setFacultyLoading] = useState(false);

  const [events, setEvents] = useState<Event[]>([]);
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10); // start with 10
  const [hasMore, setHasMore] = useState(true);
  const [loading, setLoading] = useState(false);

  const [galleryItems, setGalleryItems] = useState<GalleryItem[]>([]);
  const [galleryLoading, setGalleryLoading] = useState(false);

  const [syllabusData, setSyllabusData] = useState<SyllabusItem[]>([]);
  const [syllabusLoading, setSyllabusLoading] = useState(false);

  const department = allDepartmentsData.find((dept) => dept.slug === slug);

  const fetchEvents = useCallback(async () => {
    if (loading || !hasMore) return;
    try {
      setLoading(true);
      const response = await fetch(
        `${process.env.NEXT_PUBLIC_API_URL}/events?category=${encodeURIComponent(departmentName)}&page=${page}&limit=${limit}&sortBy=date`,
      );
      if (!response.ok) throw new Error("Failed to fetch events");
      const data = await response.json();

      if (data.data.length === 0) {
        setHasMore(false);
        return;
      }

      // Append new data (avoid duplicates)
      setEvents((prev) => [...prev, ...data.data.filter((newEvent: Event) => !prev.some((e) => e.id === newEvent.id))]);

      if (data.data.length < limit) {
        setHasMore(false);
      } else {
        // update next batch size logic
        if (page === 1) {
          setLimit(10); // 2nd fetch = +10
        } else {
          setLimit(20); // then always +20
        }
        setPage((prev) => prev + 1);
      }
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [departmentName, page, limit, hasMore, loading]);

  useEffect(() => {
    setEvents([]);
    setPage(1);
    setLimit(10);
    setHasMore(true);
    setGalleryItems([]);
    setSyllabusData([]);
  }, [departmentName]);

  useEffect(() => {
    if (hasMore && !loading) {
      fetchEvents();
    }
  }, [fetchEvents, hasMore]);

  useEffect(() => {
    if (!loading && hasMore && events.length > 0) {
      const timer = setTimeout(() => {
        fetchEvents();
      }, 1000); // auto trigger next batch after 1s
      return () => clearTimeout(timer);
    }
  }, [loading, hasMore, events.length, fetchEvents]);

  const fetchGallery = useCallback(async () => {
    if (galleryLoading || galleryItems.length > 0) return; // Prevent re-fetching if data exists (or check specific flag)
    try {
      setGalleryLoading(true);
      const response = await fetch(`${process.env.NEXT_PUBLIC_API_URL}/gallery?category=${encodeURIComponent(departmentName)}&all=true`);
      if (!response.ok) throw new Error("Failed to fetch gallery");
      const data = await response.json();

      // ✅ UPDATED logic to handle standard image URLs instead of buffer arrays
      const newItems: GalleryItem[] = (data.data || []).map((item: any) => ({
        ...item,
        image: item.imageUrl ? `${process.env.NEXT_PUBLIC_API_URL}/gallery/file/${item.imageUrl}` : item.image,
      }));

      setGalleryItems(newItems);
    } catch (err) {
      console.error(err);
    } finally {
      setGalleryLoading(false);
    }
  }, [departmentName, galleryLoading, galleryItems.length]);

  // Initial fetch for gallery when tab is selected
  useEffect(() => {
    if (selectedSection === "Gallery" && galleryItems.length === 0) {
      fetchGallery();
    }
  }, [selectedSection, galleryItems.length, fetchGallery]);

  // Fetch Academic Syllabus
  useEffect(() => {
    const fetchSyllabus = async () => {
      if (!department?.name) return;
      setSyllabusLoading(true);
      try {
        const res = await fetch(`${process.env.NEXT_PUBLIC_API_URL}/academic-syllabus?department=${encodeURIComponent(department.name)}&limit=100`);
        if (!res.ok) throw new Error("Failed to fetch academic syllabus");
        const data = await res.json();
        setSyllabusData(data.data || []);
      } catch (error) {
        console.error("Failed to fetch academic syllabus", error);
      } finally {
        setSyllabusLoading(false);
      }
    };

    if (syllabusData.length === 0) {
      fetchSyllabus();
    }
  }, [department?.name, syllabusData.length]);

  const isAIDS =
    department?.name === "Artificial Intelligence & Data Science" ||
    department?.name === "AI & DS (Proposed – for the upcoming academic year 2026–2027)";

  // Fetch Faculty Data
  useEffect(() => {
    async function fetchFaculty() {
      if (!department?.name) return;
      try {
        setFacultyLoading(true);
        const targetDept = isAIDS ? "Computer Science & Business System" : department.name;
        const url = `${process.env.NEXT_PUBLIC_API_URL}/faculty?department=${encodeURIComponent(targetDept)}&all=true`;
        const res = await fetch(url);
        const rawData = await res.json();
        const data: FacultyMember[] = rawData.map((member: any) => ({
          ...member,
          qualifications: member.qualifications?.map((q: any) => ({
            ...q,
            specialization: q.specializedArea || q.specialization || "",
          })),
        }));
        setFacultyData(data);
      } catch (err) {
        console.error("Error fetching faculty data:", err);
      } finally {
        setFacultyLoading(false);
      }
    }

    if ((selectedSection === "Faculty & Staff" || selectedSection === "Head of the Department") && facultyData.length === 0) {
      fetchFaculty();
    }
  }, [selectedSection, department?.name, facultyData.length, isAIDS]);

  // ✅ REMOVED manual sorting logic since backend handles it perfectly now!
  const teachingStaff = facultyData.filter((item) => item.type !== "Technical Staff");
  const technicalStaff = facultyData.filter((item) => item.type === "Technical Staff");

  const hodFaculty = React.useMemo(
    () => facultyData.find((f) => f.name === department?.depatmentHead?.name),
    [facultyData, department?.depatmentHead?.name],
  );

  const [hodApiData, setHodApiData] = useState<CouncilMember | null>(null);

  // Fetch HOD Data specifically
  useEffect(() => {
    async function fetchHod() {
      if (!department?.name) return;
      try {
        const targetDept = isAIDS ? "Computer Science & Business System" : department.name;
        const url = `${process.env.NEXT_PUBLIC_API_URL}/faculty?hod=true&department=${encodeURIComponent(targetDept)}`;
        const res = await fetch(url);
        if (res.ok) {
          const result = await res.json();
          if (result.data && result.data.length > 0) {
            setHodApiData(result.data[0]);
          }
        }
      } catch (err) {
        console.error("Error fetching HOD data:", err);
      }
    }

    if (selectedSection === "Head of the Department" && !hodApiData) {
      fetchHod();
    }
  }, [selectedSection, department?.name, hodApiData, isAIDS]);

  // --> ADDED "Academic Syllabus & Schema" to the tabs array <--
  const isMCAorMBA = ["mca", "mba"].includes(department?.slug || "");
   const isMCA = ["mca"].includes(department?.slug || "");

  const departmentMenuItems = isAIDS
    ? ["Department Profile", "Faculty & Staff", "Course Outcomes (CO)"]
    : isMCAorMBA
    ? ["Department Profile", "Career Prospects", "Academic Syllabus"]
    : [
        "Department Profile",
        ...(department?.name === "Artificial Intelligence & Machine Learning" || department?.name === "Mechanical Engineering"
          ? ["Career Prospects"]
          : []),
        ...(department?.name !== "Mechanical Engineering" ? ["Organisation Structure"] : []),
        "Head of the Department",
        "Faculty & Staff",
        "Academic Programmes",
        ...(department?.name === "Mechanical Engineering" ? ["Departmental Structure"] : []),
        "Academic Syllabus & Schema", // NEW TAB
        ...(department?.name === "Science & Humanities" ? ["PO"] : ["PEO & PO-PSO"]),
        "Course Outcomes (CO)",
        "Facilities",
        "Student Achievements",
        ...(department?.name !== "Information Science & Engineering" ? ["Research & Product Development"] : []),
        ...(department?.name === "Information Science & Engineering" ? ["Publications"] : []),
        ...(department?.name === "Mechanical Engineering" ? ["Faculty Excellence  and publication"] : []),
        "Magazines & Newsletters",
        "Events",
        "Gallery",
      ];

  const hodData = React.useMemo(() => {
    if (!department?.depatmentHead) return undefined;

    const sourceData = hodApiData || (hodFaculty as unknown as CouncilMember);

    return {
      ...department.depatmentHead,
      ...(sourceData
        ? {
            name: sourceData.name,
            position: sourceData.designation,
            // ✅ FIX 2: Check for hasAvatar and use API endpoint instead of buffer
            imageUrl: sourceData.hasAvatar
              ? `${process.env.NEXT_PUBLIC_API_URL}/faculty/${sourceData.id}/avatar`
              : sourceData.image || department.depatmentHead.imageUrl,
          }
        : {}),
    };
  }, [department?.depatmentHead, hodFaculty, hodApiData]);

  return (
    <section className=" text-[#1D1D1F]  overflow-hidden">
      <div className="">
        <div className="relative h-[300px] md:h-[450px] w-full flex items-center overflow-hidden bg-[#eff2f6]">
          {/* Image Container: Starts after 60% (occupies right 40%) */}
          <div className="absolute right-0 top-0 h-full w-[70%]">
            <Image src="/departmentImages/bgImageMoreAbout.png" alt="bg" fill className="object-cover object-center" />
            {/* Gradient Mask: Only applied to this 40% section to blend the hard left edge */}
            <div className="absolute inset-0 bg-gradient-to-r from-[#eff2f6] via-transparent to-transparent z-10" />
          </div>

          {/* Content Container: Occupies the left 60% */}
          <div className="w-full lg2:px-24 px-5 h-full relative z-20 flex flex-col justify-center">
            {/* Go Back Button */}
            <div className="absolute top-20">
              <Link href={`/department/${slug}`}>
                <button className="flex items-center gap-2 text-[#555]  transition-colors">
                  <ArrowLeftIcon width={20} height={20} />
                  <span className="text-lg text-[#6B6B6B]">Go Back</span>
                </button>
              </Link>
            </div>

            {/* Text Headings - Restricted to 60% width */}
            <div className="mt-12 lg:w-[70%]">
              <h1 className="text-[#333] text-xl lg:text-[32px] mb-1 font-medium">Department of</h1>
              <h2 className="text-[30px] lg:text-[56px] font-bold leading-[1.15] text-[#1D1D1F]">
                {department?.name
                  // 1. Split by ' & ' or ' and ' (case insensitive)
                  // The parentheses () keep the separator in the array
                  ?.split(/(\s+&\s+|\s+and\s+)/i)
                  .map((part, index) => (
                    <React.Fragment key={index}>
                      {/* Render the text part */}
                      {formatDepartmentName(part)}

                      {/* 2. Check if this part is a separator. If yes, add the responsive break */}
                      {/(\s+&\s+|\s+and\s+)/i.test(part) && (
                        <br className={`hidden ${!isAIDS && !isMCA && "lg:block"}`} />
                      )}
                    </React.Fragment>
                  ))}
              </h2>
            </div>
          </div>
          <div className="absolute bottom-0 left-0 right-0 h-[400px] md:h-[100px] bg-gradient-to-t from-[#fbfcfe] via-[#fbfcfe]/60 to-transparent z-[10] " />
        </div>
        <div className={`md:grid grid-cols-1 pb-14 xl:pb-32 text-[#1D1D1F] lg2:px-24 px-5 gap-3  md:grid-cols-12 md:mt-10`}>
          <div className="col-span-3">
            <div className="sticky top-20 h-fit">
              {/* Mobile Dropdown */}
              <div className="block md:hidden mb-8">
                <CustomSelect
                  value={selectedSection}
                  onChange={(e) => {
                    const newSection = e.target.value;
                    const newIndex = departmentMenuItems.indexOf(newSection);
                    if (newIndex !== -1) {
                      setSelectedIndex(newIndex);
                      setSelectedSection(newSection);
                    }
                  }}
                  options={departmentMenuItems}
                />
              </div>

              {/* Desktop Sidebar */}
              <div className="hidden md:block">
                {departmentMenuItems?.map((section, index) => (
                  <h1
                    key={index}
                    onClick={() => {
                      setSelectedIndex(index);
                      setSelectedSection(section);
                    }}
                    className={`border-b-2 text-[20px] pb-3 mb-3 border-border cursor-pointer ${
                      selectedIndex === index ? "text-[#2884CA] font-bold" : "text-textGray font-[500]"
                    }`}
                  >
                    {section}
                  </h1>
                ))}
              </div>
            </div>
          </div>
          <div className="col-span-1"></div>
          <div className="col-span-8 max-h-[70vh] md:max-h-[130vh] scrollable overflow-y-auto  pr-2">
            {selectedSection === "Department Profile" && (
              <DepartmentProfile
                annualIntake={department?.annualTake}
                keyPracticesECE={department?.keyPracticesECE}
                keyPoints={department?.keyPractices}
                data={department?.description}
              />
            )}
            {selectedSection === "Organisation Structure" && department?.organisation && <Organaisation data={department?.organisation} />}
            {selectedSection === "Head of the Department" && <Hod data={hodData} facultyProfile={hodFaculty} />}
            {selectedSection === "Faculty & Staff" && (
              <Faculty teachingStaff={teachingStaff} technicalStaff={technicalStaff} loading={facultyLoading} />
            )}

            {/* Standard Academic Programmes Logic */}
            {(selectedSection === "Academic Programmes" || selectedSection === "Academic Syllabus") && department?.academicsProgram && (
              <Academic academicsProgramEce={department.academicsProgramEce} data={department.academicsProgram} />
            )}
            {(selectedSection === "Academic Programmes" || selectedSection === "Academic Syllabus") && department?.academicsProgramEce && (
              <Academic academicsProgramEce={department.academicsProgramEce} data={department.academicsProgram} />
            )}

            {/* --> NEW PDF Academic Syllabus Schema Section Component <-- */}
            {selectedSection === "Academic Syllabus & Schema" && department?.name && (
              <AcademicSyllabusSchema departmentName={department.name} data={syllabusData} loading={syllabusLoading} />
            )}

            {selectedSection === "PO" && department?.peo && <Peo data={department.peo} deptName={department?.name} />}
            {selectedSection === "PEO & PO-PSO" && department?.peo && <Peo data={department.peo} deptName={department?.name} />}
            {selectedSection === "Course Outcomes (CO)" && !isAIDS && (
              <CourseOutCome deptName={department?.name} staticData={department?.courseOutcome} />
            )}
            {selectedSection === "Course Outcomes (CO)" && isAIDS && <CourseOutComeAiDs />}
            {selectedSection === "Facilities" && department?.facilities && <Facilities deptName={department?.name} data={department?.facilities} />}
            {selectedSection === "Student Achievements" && department?.studentAcheivemtents && (
              <StudentAchievement data={department?.studentAcheivemtents} />
            )}
            {selectedSection === "Research & Product Development" && department?.research && (
              <Research deptName={department?.name} data={department?.research} />
            )}
            {selectedSection === "Publications" && department?.publications && <Publications data={department?.publications} />}
            {selectedSection === "Faculty Excellence  and publication" && <FacultyExcellence />}
            {selectedSection === "Magazines & Newsletters" && department?.magazines && <Magazines data={department?.magazines} />}
            {selectedSection === "Events" && <Events events={events} departmentName={departmentName} />}
            {selectedSection === "Gallery" && <Gallery data={[...galleryItems, ...(department?.gallery || [])]} />}
            {selectedSection === "Career Prospects" && <CareerProspects data={department?.careerProspects} />}
            {selectedSection === "Departmental Structure" && <DepartmentalStructure data={department?.departmentalStructure} />}
          </div>
        </div>
      </div>
    </section>
  );
};

export default DepartmentDetailes;
